/**
 * 🖼️ MediaPickerModal - Selector de archivos multimedia (estilo Shopify)
 *
 * Modal que muestra imágenes existentes + opción de subir nuevas.
 * Usado en Contenido y en flujos de selección de imagen (productos, categorías, etc.)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Modal,
  TextField,
  BlockStack,
  InlineStack,
  Box,
  Thumbnail,
  Button,
  DropZone,
  Checkbox,
  Spinner,
  Scrollable,
  Icon,
  Text,
} from '@/lib/polaris';
import { SearchIcon, ImageIcon } from '@/lib/polaris-icons';
import { uploadAsset, listMediaAssets } from '@/lib/storage';
import { useAuth } from '@smarty/shared';
import { useAdminToastOptional } from '@/contexts/AdminToastContext';
import { formatFileSize } from '@/lib/utils';

export interface MediaFile {
  id: string;
  name: string;
  url: string;
  bucket: string;
  size: number;
  created_at: string;
  updated_at: string;
}

type ViewMode = 'grid' | 'list';

interface MediaPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Modo biblioteca: solo visualizar/subir. Modo picker: seleccionar y retornar. */
  mode: 'library' | 'picker';
  /** En modo picker, se llama con la URL seleccionada al confirmar */
  onSelect?: (url: string, file?: MediaFile) => void;
  /** Si true, renderiza en portal con z-index alto (para abrir desde dentro de otro modal) */
  elevateZIndex?: boolean;
}


export function MediaPickerModal({ open, onClose, mode, onSelect, elevateZIndex = false }: MediaPickerModalProps) {
  const { tenantId } = useAuth();
  const toast = useAdminToastOptional();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loadMediaFiles = useCallback(async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      // R2-first (Fase A.2): listar desde el índice media_assets (R2 no expone
      // list público). Ver docs/architecture/STORAGE-R2-RULE.md
      const assets = await listMediaAssets(tenantId);
      const allFiles: MediaFile[] = assets.map((a) => ({
        id: a.id,
        name: a.filename || a.key.split('/').pop() || a.key,
        url: a.url,
        bucket: a.category || 'media',
        size: a.size_bytes || 0,
        created_at: a.created_at,
        updated_at: a.created_at,
      }));
      setFiles(allFiles);
    } catch (error: unknown) {
      toast?.error('Error al cargar imágenes', (error as Error)?.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) loadMediaFiles();
  }, [open, tenantId, loadMediaFiles]);

  const handleUpload = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file || !tenantId) return;

      if (!file.type.startsWith('image/')) {
        toast?.error('Solo se permiten archivos de imagen');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast?.error('La imagen no debe superar los 5MB');
        return;
      }

      try {
        setUploading(true);
        // R2-first: sube a R2 vía uploadAsset (registra en media_assets).
        await uploadAsset(file, {
          category: 'media',
          identifier: file.name.replace(/\.[^.]+$/, '') || 'archivo',
          tenantId,
        });
        toast?.success('Imagen subida correctamente');
        await loadMediaFiles();
      } catch (error: unknown) {
        toast?.error('Error al subir la imagen', (error as Error)?.message);
      } finally {
        setUploading(false);
      }
    },
    [tenantId, loadMediaFiles]
  );

  const handleConfirm = useCallback(() => {
    if (mode === 'picker' && selectedIds.size > 0 && onSelect) {
      const firstId = Array.from(selectedIds)[0];
      const file = files.find((f) => f.id === firstId);
      if (file) onSelect(file.url, file);
    }
    setSelectedIds(new Set());
    // Si estamos en portal (elevateZIndex), cerrar en el siguiente tick para que el modal
    // que está debajo no interprete el desmontaje como "click outside" o pérdida de foco.
    if (elevateZIndex) {
      setTimeout(() => onClose(), 0);
    } else {
      onClose();
    }
  }, [mode, selectedIds, files, onSelect, onClose, elevateZIndex]);

  const handleClose = useCallback(() => {
    setSelectedIds(new Set());
    setSearchQuery('');
    onClose();
  }, [onClose]);

  const filteredFiles = searchQuery.trim()
    ? files.filter((f) =>
        f.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : files;

  const hasSelection = mode === 'picker' && selectedIds.size > 0;

  const sectionContent = (
    <BlockStack gap="400">
          <TextField
            label="Buscar archivos"
            labelHidden
            placeholder="Buscar archivos"
            value={searchQuery}
            onChange={setSearchQuery}
            autoComplete="off"
            prefix={<Icon source={SearchIcon} />}
          />

          <DropZone
            accept="image/*"
            allowMultiple={false}
            disabled={uploading}
            onDropAccepted={handleUpload}
            type="image"
          >
            <DropZone.FileUpload
              actionTitle="Agregar multimedia"
              actionHint="Arrastra y suelta imágenes, videos, modelos 3D y archivos"
            />
          </DropZone>

          {loading ? (
            <Box padding="800">
              <InlineStack align="center" blockAlign="center" gap="300">
                <Spinner size="large" />
                <span>Cargando archivos…</span>
              </InlineStack>
            </Box>
          ) : filteredFiles.length === 0 ? (
            <Box padding="600">
              <BlockStack gap="200" inlineAlign="center">
                <Icon source={ImageIcon} tone="subdued" />
                <p>No hay archivos. Sube una imagen arriba.</p>
              </BlockStack>
            </Box>
          ) : (
            <>
              <InlineStack align="space-between" blockAlign="center">
                <span>{filteredFiles.length} archivos</span>
                <InlineStack gap="100">
                  <Button
                    size="slim"
                    variant={viewMode === 'grid' ? 'primary' : 'tertiary'}
                    onClick={() => setViewMode('grid')}
                    accessibilityLabel="Vista en cuadrícula"
                  >
                    Grid
                  </Button>
                  <Button
                    size="slim"
                    variant={viewMode === 'list' ? 'primary' : 'tertiary'}
                    onClick={() => setViewMode('list')}
                    accessibilityLabel="Vista en lista"
                  >
                    Lista
                  </Button>
                </InlineStack>
              </InlineStack>

              <Scrollable style={{ maxHeight: 320 }}>
                {viewMode === 'grid' ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: 'var(--p-space-300)',
                    }}
                  >
                    {filteredFiles.map((file) => (
                      <div
                        key={file.id}
                        style={{
                          position: 'relative',
                          cursor: mode === 'picker' ? 'pointer' : 'default',
                          borderRadius: 'var(--p-border-radius-200)',
                          overflow: 'hidden',
                          border: selectedIds.has(file.id)
                            ? '2px solid var(--p-color-border-focus)'
                            : '1px solid var(--p-color-border)',
                        }}
                        onClick={() => {
                          if (mode === 'picker') {
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(file.id)) next.delete(file.id);
                              else {
                                next.clear();
                                next.add(file.id);
                              }
                              return next;
                            });
                          }
                        }}
                      >
                        {mode === 'picker' && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              zIndex: 2,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              label=""
                              labelHidden
                              checked={selectedIds.has(file.id)}
                              onChange={() => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(file.id)) next.delete(file.id);
                                  else {
                                    next.clear();
                                    next.add(file.id);
                                  }
                                  return next;
                                });
                              }}
                            />
                          </div>
                        )}
                        <Thumbnail source={file.url} alt={file.name} size="medium" />
                        <Box padding="100">
                          <p
                            style={{
                              fontSize: 'var(--p-font-size-275)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {file.name.split('/').pop()}
                          </p>
                        </Box>
                      </div>
                    ))}
                  </div>
                ) : (
                  <BlockStack gap="100">
                    {filteredFiles.map((file) => (
                      <div
                        key={file.id}
                        onClick={() => {
                          if (mode === 'picker') {
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(file.id)) next.delete(file.id);
                              else {
                                next.clear();
                                next.add(file.id);
                              }
                              return next;
                            });
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--p-space-300)',
                          padding: 'var(--p-space-200)',
                          borderRadius: 'var(--p-border-radius-200)',
                          cursor: mode === 'picker' ? 'pointer' : 'default',
                          backgroundColor: selectedIds.has(file.id)
                            ? 'var(--p-color-bg-surface-selected)'
                            : undefined,
                        }}
                      >
                        {mode === 'picker' && (
                          <Checkbox
                            label=""
                            labelHidden
                            checked={selectedIds.has(file.id)}
                            onChange={() => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(file.id)) next.delete(file.id);
                                else {
                                  next.clear();
                                  next.add(file.id);
                                }
                                return next;
                              });
                            }}
                          />
                        )}
                        <Thumbnail source={file.url} alt={file.name} size="small" />
                        <BlockStack gap="050">
                          <Text as="span" variant="bodyMd">
                            {file.name.split('/').pop()}
                          </Text>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {formatFileSize(file.size)}
                          </Text>
                        </BlockStack>
                      </div>
                    ))}
                  </BlockStack>
                )}
              </Scrollable>
            </>
          )}
        </BlockStack>
  );

  if (!open) return null;

  if (elevateZIndex) {
    return createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          role="presentation"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
          }}
          onClick={handleClose}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="media-picker-title"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            zIndex: 1,
            background: 'var(--p-color-bg)',
            borderRadius: 'var(--p-border-radius-300)',
            maxWidth: 640,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: 'var(--p-shadow-500)',
            padding: 'var(--p-space-400)',
          }}
        >
          <h2 id="media-picker-title" style={{ marginTop: 0 }}>
            Seleccionar archivo
          </h2>
          {sectionContent}
          <InlineStack gap="200" blockAlign="center" style={{ marginTop: 'var(--p-space-400)' }}>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={mode === 'picker' && !hasSelection}
            >
              Listo
            </Button>
          </InlineStack>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Seleccionar archivo"
      size="large"
      primaryAction={{
        content: 'Listo',
        onAction: handleConfirm,
        disabled: mode === 'picker' && !hasSelection,
      }}
      secondaryActions={[{ content: 'Cancelar', onAction: handleClose }]}
    >
      <Modal.Section>{sectionContent}</Modal.Section>
    </Modal>
  );
}

export default MediaPickerModal;
